import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const AttendanceHistory = () => {
  const routes = all_routes;

  const [children] = useState([
    { id: "1", name: "Emma Johnson", grade: "10th", class: "10-A" },
    { id: "2", name: "James Johnson", grade: "8th", class: "8-B" },
  ]);

  const [selectedChildId, setSelectedChildId] = useState("1");
  const [selectedMonth, setSelectedMonth] = useState("2024-08");
  const [viewType, setViewType] = useState<"daily" | "weekly">("weekly");

  const selectedChild = children.find((child) => child.id === selectedChildId);

  // Mock attendance data
  const attendanceData = {
    summary: {
      totalDays: 22,
      presentDays: 20,
      absentDays: 1,
      lateDays: 1,
      percentage: 90.9,
    },
    calendar: [
      { date: "2024-08-01", status: "present" },
      { date: "2024-08-02", status: "present" },
      { date: "2024-08-03", status: "late" },
      { date: "2024-08-04", status: "absent" },
      { date: "2024-08-05", status: "present" },
      { date: "2024-08-06", status: "present" },
      { date: "2024-08-07", status: "present" },
      { date: "2024-08-08", status: "present" },
      { date: "2024-08-09", status: "present" },
      { date: "2024-08-10", status: "holiday" },
      { date: "2024-08-11", status: "holiday" },
      { date: "2024-08-12", status: "present" },
      { date: "2024-08-13", status: "present" },
      { date: "2024-08-14", status: "present" },
      { date: "2024-08-15", status: "present" },
      { date: "2024-08-16", status: "present" },
      { date: "2024-08-17", status: "holiday" },
      { date: "2024-08-18", status: "holiday" },
      { date: "2024-08-19", status: "present" },
      { date: "2024-08-20", status: "present" },
      { date: "2024-08-21", status: "present" },
      { date: "2024-08-22", status: "present" },
    ],
  };

  const getWeekOfYear = (date: string): string => {
    const d = new Date(date);
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const days = Math.floor(
      (d.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
    );
    const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${weekNumber.toString().padStart(2, "0")}`;
  };

  const groupedData =
    viewType === "weekly"
      ? attendanceData.calendar.reduce((groups, record) => {
          const week = getWeekOfYear(record.date);
          if (!groups[week]) groups[week] = [];
          groups[week].push(record);
          return groups;
        }, {} as Record<string, any[]>)
      : attendanceData.calendar.reduce((groups, record) => {
          if (!groups[record.date]) groups[record.date] = [];
          groups[record.date].push(record);
          return groups;
        }, {} as Record<string, any[]>);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "present":
        return "success";
      case "absent":
        return "danger";
      case "late":
        return "warning";
      case "holiday":
        return "secondary";
      default:
        return "light";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "present":
        return "ti-check";
      case "absent":
        return "ti-x";
      case "late":
        return "ti-clock";
      case "holiday":
        return "ti-calendar-off";
      default:
        return "ti-circle";
    }
  };

  const getGroupTitle = (key: string): string => {
    if (viewType === "weekly") {
      const [year, week] = key.split("-W");
      return `Week ${week}, ${year}`;
    } else {
      return new Date(key).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const renderCalendarDay = (dayData: any) => {
    const day = new Date(dayData.date).getDate();
    return (
      <div
        key={dayData.date}
        className={`p-2 text-center border rounded ${
          dayData.status === "holiday" ? "bg-light text-muted" : ""
        }`}
      >
        <div className="fw-semibold">{day}</div>
        <div
          className={`avatar avatar-xs bg-${getStatusColor(
            dayData.status
          )} mt-1`}
        >
          <i
            className={`ti ${getStatusIcon(dayData.status)} text-white fs-12`}
          ></i>
        </div>
      </div>
    );
  };

  const renderDayCard = (dayData: any) => {
    return (
      <div key={dayData.date} className="col-md-6 col-lg-4">
        <div className="card border">
          <div className="card-body p-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-1">
                  {new Date(dayData.date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </h6>
                <span className={`badge bg-${getStatusColor(dayData.status)}`}>
                  {dayData.status.charAt(0).toUpperCase() +
                    dayData.status.slice(1)}
                </span>
              </div>
              <div
                className={`avatar avatar-sm bg-${getStatusColor(
                  dayData.status
                )}`}
              >
                <i
                  className={`ti ${getStatusIcon(dayData.status)} text-white`}
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <div className="d-flex align-items-center">
                <Link
                  to={routes.parentMenu}
                  className="btn btn-outline-secondary btn-sm me-3"
                >
                  <i className="ti ti-arrow-left"></i>
                </Link>
                <div>
                  <h3 className="page-title mb-1">Attendance History</h3>
                  <p className="text-muted mb-0">Detailed attendance records</p>
                </div>
              </div>
            </div>
            <div className="col-auto">
              {children.length > 1 && (
                <div className="dropdown">
                  <button
                    className="btn btn-outline-primary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-user me-2"></i>
                    {selectedChild?.name}
                  </button>
                  <ul className="dropdown-menu">
                    {children.map((child) => (
                      <li key={child.id}>
                        <button
                          className={`dropdown-item ${
                            selectedChildId === child.id ? "active" : ""
                          }`}
                          onClick={() => setSelectedChildId(child.id)}
                        >
                          {child.name} - {child.grade} {child.class}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Select Month</label>
                <select
                  className="form-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="2024-08">August 2024</option>
                  <option value="2024-07">July 2024</option>
                  <option value="2024-06">June 2024</option>
                  <option value="2024-05">May 2024</option>
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">View Type</label>
                <div className="btn-group w-100">
                  <button
                    className={`btn ${
                      viewType === "daily"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => setViewType("daily")}
                  >
                    Daily
                  </button>
                  <button
                    className={`btn ${
                      viewType === "weekly"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => setViewType("weekly")}
                  >
                    Weekly
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-primary mb-2">
                    <i className="ti ti-calendar text-white fs-2"></i>
                  </div>
                  <h4 className="text-primary">
                    {attendanceData.summary.totalDays}
                  </h4>
                  <p className="mb-0">Total Days</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-check text-white fs-2"></i>
                  </div>
                  <h4 className="text-success">
                    {attendanceData.summary.presentDays}
                  </h4>
                  <p className="mb-0">Present</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-danger mb-2">
                    <i className="ti ti-x text-white fs-2"></i>
                  </div>
                  <h4 className="text-danger">
                    {attendanceData.summary.absentDays}
                  </h4>
                  <p className="mb-0">Absent</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning mb-2">
                    <i className="ti ti-percentage text-white fs-2"></i>
                  </div>
                  <h4 className="text-warning">
                    {attendanceData.summary.percentage}%
                  </h4>
                  <p className="mb-0">Attendance Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance View */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                Attendance Records - {viewType === "daily" ? "Daily" : "Weekly"}{" "}
                View
              </h5>
            </div>
            <div className="card-body">
              {/* Legend */}
              <div className="row mb-4">
                <div className="col d-flex justify-content-center gap-4">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xs bg-success me-2">
                      <i className="ti ti-check text-white fs-12"></i>
                    </div>
                    <small>Present</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xs bg-danger me-2">
                      <i className="ti ti-x text-white fs-12"></i>
                    </div>
                    <small>Absent</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xs bg-warning me-2">
                      <i className="ti ti-clock text-white fs-12"></i>
                    </div>
                    <small>Late</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xs bg-secondary me-2">
                      <i className="ti ti-calendar-off text-white fs-12"></i>
                    </div>
                    <small>Holiday</small>
                  </div>
                </div>
              </div>

              {/* Display Content */}
              {viewType === "weekly"
                ? // Weekly grouped view
                  Object.entries(groupedData).map(([weekKey, days]) => (
                    <div key={weekKey} className="mb-4">
                      <h6 className="text-primary mb-3 border-bottom pb-2">
                        {getGroupTitle(weekKey)}
                      </h6>
                      <div
                        className="d-grid gap-2 mb-3"
                        style={{ gridTemplateColumns: "repeat(7, 1fr)" }}
                      >
                        <div className="text-center fw-semibold text-muted p-2">
                          Sun
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Mon
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Tue
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Wed
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Thu
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Fri
                        </div>
                        <div className="text-center fw-semibold text-muted p-2">
                          Sat
                        </div>
                        {days.map((day) => renderCalendarDay(day))}
                      </div>
                    </div>
                  ))
                : // Daily card view
                  Object.entries(groupedData).map(([dateKey, dayArray]) => (
                    <div key={dateKey} className="mb-4">
                      <h6 className="text-primary mb-3 border-bottom pb-2">
                        {getGroupTitle(dateKey)}
                      </h6>
                      <div className="row">
                        {dayArray.map((day) => renderDayCard(day))}
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttendanceHistory;
